import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { FileText, Download, Upload, Trash2, Lock, Image } from 'lucide-react';
import { publicationsAPI, authAPI, analyticsAPI } from '../utils/api';

const Publications = () => {
  const [publications, setPublications] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPublications();
    
    // Check if admin is logged in
    const adminToken = sessionStorage.getItem('adminToken');
    if (adminToken) {
      setIsAdmin(true);
    }
  }, []);

  const fetchPublications = async () => {
    try {
      setIsLoading(true);
      const response = await publicationsAPI.getAll();
      setPublications(response.publications || []);
    } catch (error) {
      console.error('Error fetching publications:', error);
      toast({
        title: "Error",
        description: "Failed to load publications",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminLogin = async () => {
    try {
      const response = await authAPI.login(adminPassword);
      if (response.success && response.token) {
        sessionStorage.setItem('adminToken', response.token);
        setIsAdmin(true);
        toast({
          title: "Admin Access Granted",
          description: "You can now upload and manage publications.",
        });
        setAdminPassword('');
      }
    } catch (error) {
      toast({
        title: "Access Denied",
        description: "Incorrect password.",
        variant: "destructive"
      });
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      toast({
        title: "Invalid File",
        description: "Please select a PDF file.",
        variant: "destructive"
      });
    }
  };

  const handleThumbnailSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedThumbnail(file);
    } else {
      toast({
        title: "Invalid Image",
        description: "Please select a JPG or PNG image.",
        variant: "destructive"
      });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadTitle) {
      toast({
        title: "Missing Information",
        description: "Please provide a title and select a PDF file.",
        variant: "destructive"
      });
      return;
    }

    setIsUploading(true);

    try {
      await publicationsAPI.upload(uploadTitle, selectedFile, selectedThumbnail);
      
      toast({
        title: "Upload Successful",
        description: "Your publication has been added.",
      });

      setSelectedFile(null);
      setSelectedThumbnail(null);
      setUploadTitle('');
      
      // Refresh publications list
      await fetchPublications();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Failed to upload publication",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await publicationsAPI.delete(id);
      toast({
        title: "Deleted",
        description: "Publication removed successfully.",
      });
      await fetchPublications();
    } catch (error) {
      toast({
        title: "Delete Failed",
        description: "Failed to delete publication",
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    setIsAdmin(false);
    sessionStorage.removeItem('adminToken');
    toast({
      title: "Logged Out",
      description: "Admin session ended.",
    });
  };

  if (isLoading) {
    return (
      <div className="relative z-10 min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <p className="text-white">Loading publications...</p>
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-16 px-6">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4">Publications & Research</h1>
          <p className="text-xl text-gray-300">Professional documents and research papers</p>
          <div className="h-1 w-32 bg-gradient-to-r from-blue-600 to-sky-500 mx-auto rounded-full mt-6"></div>
        </div>

        {/* Admin Section */}
        {!isAdmin ? (
          <Card className="p-8 border-blue-900/30 shadow-xl bg-[#060f1e]/80 backdrop-blur mb-8 max-w-md mx-auto">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-900/40 to-sky-900/40 rounded-full mx-auto mb-4">
                <Lock className="h-8 w-8 text-sky-400" />
              </div>
              <h3 className="text-xl font-bold text-white">Admin Access</h3>
              <p className="text-sm text-gray-300 mt-2">Login to upload and manage publications</p>
            </div>
            <div className="space-y-4">
              <Input
                type="password"
                placeholder="Enter admin password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
                className="bg-blue-950/20 border-blue-900/30 text-white placeholder:text-gray-500"
              />
              <Button
                onClick={handleAdminLogin}
                className="w-full bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white"
              >
                Login
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="p-8 border-blue-900/30 shadow-xl bg-gradient-to-br from-blue-950/40 to-sky-950/40 backdrop-blur mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">Upload New Publication</h3>
              <Button onClick={handleLogout} variant="outline" className="border-red-600 text-red-400 hover:bg-red-950/20">
                Logout
              </Button>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Document Title *</label>
                <Input
                  placeholder="Enter document title"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  className="bg-blue-950/20 border-blue-900/30 text-white placeholder:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">PDF File *</label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="cursor-pointer bg-blue-950/20 border-blue-900/30 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Image className="inline h-4 w-4 mr-1" />
                  Thumbnail (JPG/PNG)
                </label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailSelect}
                  className="cursor-pointer bg-blue-950/20 border-blue-900/30 text-white"
                />
              </div>
            </div>
            <div className="mt-4 space-y-3">
              {selectedFile && (
                <div className="p-3 bg-blue-950/20 rounded-lg">
                  <p className="text-sm text-gray-300">
                    PDF: <span className="font-medium text-white">{selectedFile.name}</span>
                  </p>
                </div>
              )}
              {selectedThumbnail && (
                <div className="p-3 bg-blue-950/20 rounded-lg flex items-center space-x-3">
                  <img 
                    src={URL.createObjectURL(selectedThumbnail)} 
                    alt="Thumbnail preview" 
                    className="h-16 w-16 object-cover rounded"
                  />
                  <p className="text-sm text-gray-300">
                    Thumbnail: <span className="font-medium text-white">{selectedThumbnail.name}</span>
                  </p>
                </div>
              )}
            </div>
            <Button
              onClick={handleUpload}
              disabled={isUploading || !selectedFile || !uploadTitle}
              className="mt-6 bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white"
            >
              {isUploading ? 'Uploading...' : 'Upload Publication'}
              <Upload className="ml-2 h-4 w-4" />
            </Button>
          </Card>
        )}

        {/* Publications List */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {publications.map((pub) => (
            <Card key={pub.id} className="p-6 border-blue-900/30 shadow-xl bg-[#060f1e]/80 backdrop-blur hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-300">
              {/* Thumbnail or Default Icon */}
              <div className="bg-gradient-to-br from-blue-900/40 to-sky-900/40 rounded-lg mb-4 flex items-center justify-center overflow-hidden">
                {pub.thumbnail ? (
                  <img 
                    src={pub.thumbnail} 
                    alt={pub.title}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="p-8">
                    <FileText className="h-20 w-20 text-sky-400" />
                  </div>
                )}
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{pub.title}</h3>
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-300">Size:</span>
                  <span className="text-white font-medium">{pub.fileSize}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-300">Uploaded:</span>
                  <Badge className="bg-blue-900/30 text-sky-300 border-0">
                    {new Date(pub.uploadDate).toLocaleDateString()}
                  </Badge>
                </div>
                {isAdmin && (
                  <div className="flex items-center justify-between text-sm pt-2 border-t border-blue-900/30">
                    <span className="text-gray-400 text-xs">File:</span>
                    <span className="text-gray-400 text-xs truncate ml-2">{pub.fileName}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    analyticsAPI.trackEvent('download', pub.title);
                    window.open(pub.fileUrl, '_blank');
                  }}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
                {isAdmin && (
                  <Button
                    onClick={() => handleDelete(pub.id)}
                    variant="outline"
                    className="border-red-600 text-red-400 hover:bg-red-950/20"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>

        {publications.length === 0 && (
          <div className="text-center py-12">
            <FileText className="h-16 w-16 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">No publications available yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Publications;